/**
 * Authentication Service
 * Handles login, registration, and authentication flows
 *
 * @author Varun Pandey
 */

import type { AxiosError } from "axios";
import apiClient from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { storageManager } from "@/storage/storageManager";
import { STORAGE_KEYS } from "@/storage/storageKeys";
import { AUTH_COOKIE_NAMES, deleteCookie, setAuthRoleCookies } from "@/utils/cookies";
import { useAuthStore } from "@/stores/authStore";
import type { User, Category } from "@/api/types";
import type { ArtistDetailsData } from "@/types/artistDetails";
import { getAppleSignInProfile } from "@/auth/appleSignInStorage";
import { getNativeAuthTokensFromCookies } from "@/utils/nativeAuthCookies";

/** Email/password login payload */
export type LoginCredentials =
  | { email: string; password: string }
  | { phone: string; password: string };

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  phone?: string;
}

export interface SignupRegisterPayload {
  email: string;
  password: string;
  confirm_password: string;
  phone: string;
}

export interface OnboardingPayload {
  category: string;
  subcategories: string[];
  professional_name: string;
  full_name: string;
  profile_managed_by: "self" | "family" | "agency" | "manager";
  agency_name?: string;
  relation?: string;
  phone?: string;
  email?: string;
  password?: string;
  confirm_password?: string;
  additional_email?: string;
}

export interface CheckExistenceResponse {
  email?: boolean;
  phone?: boolean;
}

/** Login API success response (200) - metadata for routing after login */
export interface LoginResponseData {
  onboarding_completed: boolean;
  role: string;
  artists?: Array<{ slug: string; professional_name: string; profile_pic: string }>;
}

/** Result from login/verifyOtp with user and routing metadata */
export interface LoginResult {
  user: User;
  loginData?: LoginResponseData;
}

/** Onboarding API success response (200) */
export interface OnboardingResponse {
  success: boolean;
  data: {
    onboarding_completed: boolean;
    user_type: string;
    artist?: { professional_name: string; slug: string; category: string };
  };
}

/** SSO signup/login API (POST api/v2/auth/signup/sso/) response */
export interface SignupSsoResponse {
  success: boolean;
  data: {
    onboarding_completed: boolean;
    created: boolean;
    source: string;
    user: {
      email: string;
      phone: string | null;
      first_name: string;
      last_name: string;
      full_name?: string;
      name?: string;
    };
    artist?: { professional_name: string | null; slug: string | null };
    role?: string;
    artists?: Array<{ slug: string; professional_name: string; profile_pic: string }>;
  };
}

/** Result from signupSso for routing (onboarding_completed, user info, role) */
export interface SignupSsoResult {
  onboarding_completed: boolean;
  created: boolean;
  source: string;
  user: SignupSsoResponse["data"]["user"];
  artist?: SignupSsoResponse["data"]["artist"];
  role: string;
  artists?: LoginResponseData["artists"];
}

export interface AppleSsoProfile {
  email?: string;
  fullName?: string;
  givenName?: string;
  familyName?: string;
}

function resolveSsoUserFullName(user: SignupSsoResponse["data"]["user"]): string {
  const fromParts = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  if (fromParts) return fromParts;
  const fullName = typeof user.full_name === "string" ? user.full_name.trim() : "";
  if (fullName) return fullName;
  const name = typeof user.name === "string" ? user.name.trim() : "";
  return name;
}

/** Navigate after SSO sign-in/sign-up based on onboarding_completed and role. */
export async function handleSsoResult(
  result: SignupSsoResult,
  navigate: (path: string, options?: { state?: unknown }) => void,
  fetchArtistDetails: () => Promise<ArtistDetailsData | null>,
  appleProfile?: AppleSsoProfile,
): Promise<void> {
  await persistTokensFromCookieJarIfNeeded();

  if (!result.onboarding_completed) {
    const storedApple = await getAppleSignInProfile();
    const appleFullName =
      appleProfile?.fullName?.trim() ||
      [appleProfile?.givenName, appleProfile?.familyName].filter(Boolean).join(" ").trim() ||
      storedApple?.fullName?.trim() ||
      "";
    const backendFullName = resolveSsoUserFullName(result.user);
    const fullName = appleFullName || backendFullName;
    const email =
      appleProfile?.email?.trim() ||
      storedApple?.email?.trim() ||
      result.user.email;

    if ((result.source || appleProfile) && (result.source === "apple" || appleProfile)) {
      console.log("[AppleSignIn] handleSsoResult merge:", {
        appleProfileFullName: appleProfile?.fullName ?? "(empty)",
        appleProfileGiven: appleProfile?.givenName ?? "(empty)",
        appleProfileFamily: appleProfile?.familyName ?? "(empty)",
        storedFullName: storedApple?.fullName ?? "(empty)",
        backendFullName: backendFullName || "(empty)",
        mergedFullName: fullName || "(empty)",
        mergedEmail: email || "(empty)",
        backendUser: {
          first_name: result.user.first_name,
          last_name: result.user.last_name,
          full_name: result.user.full_name,
          email: result.user.email,
        },
      });
    }

    // Persist signup progress so user can resume from category step if they drop off mid-onboarding
    try {
      await storageManager.set(STORAGE_KEYS.SIGNUP_PROGRESS, {
        step: 4,
        source: (result.source || "normal") as string,
        fullName: fullName || undefined,
        email: email || undefined,
        // Phone might be needed for certain sources (e.g. google) on final step
        phoneNumber: result.user.phone ?? undefined,
      });
    } catch (err) {
      console.error("Failed to persist signup progress from SSO result:", err);
    }

    navigate("/signup", {
      state: {
        startStep: 4,
        source: (result.source || "normal") as string,
        fullName: fullName || undefined,
        email: email || undefined,
      },
    });
    return;
  }
  const normalizedRole = (result.role ?? "individual").trim();
  const isAgency =
    normalizedRole === "agency" ||
    normalizedRole === "agency_owner" ||
    normalizedRole === "sub_user";
  setAuthRoleCookies(isAgency ? "agency" : normalizedRole);
  if (isAgency) {
    navigate("/agency-home");
    return;
  }
  await fetchArtistDetails();
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(STORAGE_KEYS.LOGIN_TO_USER_HOME, "1");
  }
  navigate("/user-home");
}

/** Extract user-friendly message from auth API error */
export function getAuthErrorMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  const axiosError = error as AxiosError<{
    message?: string;
    detail?: string;
    error?: string | { message?: unknown; code?: string; details?: unknown };
    errors?: Record<string, string[]>;
  }>;
  const data = axiosError.response?.data;
  if (!data) {
    const status = axiosError.response?.status;
    if (status === 401)
      return "Invalid credentials. Please check your email/phone and password.";
    if (status === 403) return "Access denied.";
    if (status === 404) return "Account not found.";
    if (status && status >= 500) return "Server error. Please try again later.";
    return fallback;
  }
  if (typeof data.message === "string" && data.message.trim())
    return data.message;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  if (data.error && typeof data.error === "object") {
    const errObj = data.error as {
      message?: unknown;
      code?: string;
      details?: unknown;
    };

    // 1) Simple string message on nested error
    if (typeof errObj.message === "string" && errObj.message.trim()) {
      return errObj.message.trim();
    }

    // 2) Array-based message structure, e.g.
    //    message: [ { professional_name: "This professional name is already taken..." } ]
    if (Array.isArray(errObj.message)) {
      for (const item of errObj.message) {
        if (typeof item === "string" && item.trim()) {
          return item.trim();
        }
        if (item && typeof item === "object") {
          const values = Object.values(item as Record<string, unknown>);
          const firstString = values.find(
            (v) => typeof v === "string" && v.trim(),
          ) as string | undefined;
          if (firstString) return firstString.trim();
        }
      }
    }

    // 3) details object, e.g.
    //    details: { professional_name: ["This professional name is already taken..."] }
    if (errObj.details && typeof errObj.details === "object") {
      const details = errObj.details as Record<string, unknown>;
      for (const value of Object.values(details)) {
        if (Array.isArray(value)) {
          const first = value.find(
            (v) => typeof v === "string" && v.trim(),
          ) as string | undefined;
          if (first) return first.trim();
        } else if (typeof value === "string" && value.trim()) {
          return value.trim();
        }
      }
    }
  }
  if (typeof data.error === "string" && data.error.trim()) return data.error;
  if (data.errors && typeof data.errors === "object") {
    const first = Object.values(data.errors).flat().find(Boolean);
    if (typeof first === "string") return first;
  }
  const status = axiosError.response?.status;
  if (status === 401)
    return "Invalid credentials. Please check your email/phone and password.";
  if (status === 403) return "Access denied.";
  return fallback;
}

/**
 * Auth APIs use cookie-based auth: tokens come in Set-Cookie, not response body.
 * Parses response for tokens (if present) and user/metadata.
 * Does not throw when tokens are missing - cookies are sent automatically by the browser.
 */
function parseAuthResponse(data: Record<string, unknown>): {
  user: User;
  accessToken: string | undefined;
  refreshToken: string | undefined;
} {
  const inner = (data.data as Record<string, unknown>) ?? data;
  const accessToken = (
    data.accessToken ??
    data.access_token ??
    data.access ??
    data.token ??
    inner.accessToken ??
    inner.access_token ??
    inner.access ??
    inner.token
  ) as string | undefined;
  const refreshToken = (
    data.refreshToken ??
    data.refresh_token ??
    data.refresh ??
    inner.refreshToken ??
    inner.refresh_token ??
    inner.refresh
  ) as string | undefined;

  // User may be in body, or build minimal user from metadata (for cookie-based auth)
  let user = (data.user ?? inner.user) as User | undefined;
  const artists = Array.isArray(inner?.artists) ? inner.artists : [];
  const resolvedUserId = extractAuthUserId(inner, artists);

  if (!user && inner) {
    const rawRole = typeof inner.role === "string" ? inner.role.trim() : "individual";
    const normalizedRole =
      rawRole === "agency" ||
      rawRole === "agency_owner" ||
      rawRole === "sub_user"
        ? "agency"
        : rawRole === "influencer"
          ? "influencer"
          : "artist";
    const firstArtist = artists[0] as { professional_name?: string } | undefined;
    const name = firstArtist?.professional_name ?? (inner.professional_name as string) ?? "";
    user = {
      id: resolvedUserId,
      email: typeof inner.email === "string" ? inner.email : "",
      name: String(name),
      role: normalizedRole as User["role"],
      createdAt: "",
      updatedAt: "",
    };
  } else if (user && resolvedUserId && !String(user.id ?? "").trim()) {
    user = { ...user, id: resolvedUserId };
  }
  if (!user) user = { id: resolvedUserId, email: "", name: "", role: "artist", createdAt: "", updatedAt: "" };
  return { user, accessToken, refreshToken };
}

function extractAuthUserId(
  inner: Record<string, unknown> | undefined,
  artists: unknown[],
): string {
  if (!inner) return "";
  const nestedUser = inner.user as Record<string, unknown> | undefined;
  const firstArtist = artists[0] as Record<string, unknown> | undefined;
  const numericCandidates: unknown[] = [
    inner.id,
    inner.user_id,
    inner.artist_user_id,
    nestedUser?.id,
    nestedUser?.user_id,
    firstArtist?.user_id,
    firstArtist?.artist_user_id,
    firstArtist?.id,
  ];
  for (const value of numericCandidates) {
    if (value == null) continue;
    const s = String(value).trim();
    if (!s) continue;
    const n = Number(s);
    if (Number.isFinite(n) && n > 0) return s;
  }
  const slugCandidates: unknown[] = [
    inner.artist_slug,
    firstArtist?.slug,
    nestedUser?.artist_slug,
  ];
  for (const value of slugCandidates) {
    if (value == null) continue;
    const s = String(value).trim();
    if (s) return s;
  }
  return "";
}

async function persistTokensFromCookieJarIfNeeded(): Promise<void> {
  const store = useAuthStore.getState();
  if (store.accessToken?.trim()) return;

  const { access, refresh } = await getNativeAuthTokensFromCookies();
  if (!access) {
    console.warn("[AUTH] No token in login response — cookie-only auth, cookie read failed");
    return;
  }

  await store.setTokens(access, refresh ?? "");
  console.log("[AUTH] Token saved from cookie jar after login:", access.slice(0, 20));
}

async function persistAuthTokensFromResponse(
  accessToken: string | undefined,
  refreshToken: string | undefined,
): Promise<void> {
  const store = useAuthStore.getState();
  if (accessToken) {
    await store.setTokens(accessToken, refreshToken ?? "");
    return;
  }
  await persistTokensFromCookieJarIfNeeded();
}

function extractLoginResponseData(data: Record<string, unknown>): LoginResponseData | undefined {
  const inner = (data.data as Record<string, unknown>) ?? data;
  const completed = inner.onboarding_completed;
  const role = inner.role;
  if (typeof completed !== "boolean" && role === undefined) return undefined;
  return {
    onboarding_completed: completed === true,
    role: typeof role === "string" ? role : "individual",
    artists: Array.isArray(inner.artists) ? inner.artists as LoginResponseData["artists"] : undefined,
  };
}

export interface SimilarProfilesApiItem {
  id: string;
  slug?: string;
  profile_pic?: string;
  professional_name: string;
  masked_email?: string;
  masked_phone?: string;
}

export interface SimilarProfilesApiResponse {
  items: SimilarProfilesApiItem[];
  count: number;
  next: string | null;
  previous: string | null;
  pageSize: number;
}

export class AuthService {
  /**
   * Login with email and password, or phone and password.
   * Request body is either `{ email, password }` or `{ phone, password }` per backend contract.
   * Returns user and login metadata (onboarding_completed, role) for routing.
   */
  static async login(credentials: LoginCredentials): Promise<LoginResult> {
    const store = useAuthStore.getState();
    store.setLoading(true);

    try {
      const response = await apiClient.post(
        API_ENDPOINTS.AUTH.LOGIN,
        credentials,
      );
      const data = response.data as Record<string, unknown>;
      const { user, accessToken, refreshToken } = parseAuthResponse(data);

      await persistAuthTokensFromResponse(accessToken, refreshToken);
      store.setUser(user);

      const loginData = extractLoginResponseData(data);
      return { user, loginData };
    } catch (error) {
      throw error;
    } finally {
      store.setLoading(false);
    }
  }

  /**
   * Request OTP for phone login. On 200, caller should show OTP input.
   */
  static async requestOtp(phone: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.OTP_REQUEST, { phone });
  }

  /**
   * Check existence of email or phone before signup.
   * Payload can contain either { email } or { phone }.
   * API returns { success, data: { email?: boolean; phone?: boolean } }.
   */
  static async checkExistence(payload: { email?: string; phone?: string }): Promise<CheckExistenceResponse> {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.CHECK_EXISTENCE, payload);
    const raw = response.data as any;
    const inner = raw && typeof raw === "object" && raw.data && typeof raw.data === "object" ? raw.data : raw;
    return {
      email: typeof inner.email === "boolean" ? inner.email : undefined,
      phone: typeof inner.phone === "boolean" ? inner.phone : undefined,
    };
  }

  /**
   * Send OTP for signup. On 200, caller should show verification code step.
   */
  static async signupSendOtp(phone: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.SIGNUP_OTP_SEND, { phone });
  }

  /**
   * Verify OTP for signup. On 200, caller can proceed to signup register.
   */
  static async signupVerifyOtp(phone: string, otp: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.SIGNUP_OTP_VERIFY, {
      phone,
      otp: parseInt(otp, 10),
    });
  }

  /**
   * Complete signup with email, password, confirm_password, phone. On 200, caller advances to next step.
   */
  static async signupRegister(
    data: SignupRegisterPayload,
  ): Promise<User | void> {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.SIGNUP, data);
    const data_ = response.data as Record<string, unknown>;
    const { user, accessToken, refreshToken } = parseAuthResponse(data_);
    const store = useAuthStore.getState();
    await persistAuthTokensFromResponse(accessToken, refreshToken);
    store.setUser(user);
    return user;
  }

  /**
   * Verify OTP and complete phone login. On success sets tokens and user, returns user and login metadata.
   */
  static async verifyOtp(phone: string, otp: string): Promise<LoginResult> {
    const store = useAuthStore.getState();
    store.setLoading(true);

    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.OTP_VERIFY, {
        phone,
        otp,
      });
      const data = response.data as Record<string, unknown>;
      const { user, accessToken, refreshToken } = parseAuthResponse(data);

      await persistAuthTokensFromResponse(accessToken, refreshToken);
      store.setUser(user);

      const loginData = extractLoginResponseData(data);
      return { user, loginData };
    } catch (error) {
      throw error;
    } finally {
      store.setLoading(false);
    }
  }

  /**
   * Register new user
   */
  static async register(data: RegisterData): Promise<User> {
    const store = useAuthStore.getState();
    store.setLoading(true);

    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.REGISTER, data);
      const { user, accessToken, refreshToken } = response.data;

      await store.setTokens(accessToken, refreshToken);
      store.setUser(user);

      return user;
    } catch (error: any) {
      // Error is thrown and can be caught by the caller
      throw error;
    } finally {
      store.setLoading(false);
    }
  }

  /**
   * Logout: POST to API; only on 200 clear cookies and store. Returns true if cleared and logged out, false otherwise.
   */
  static async logout(): Promise<boolean> {
    const store = useAuthStore.getState();

    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
      if (response.status === 200) {
        deleteCookie(AUTH_COOKIE_NAMES.ROLE);
        deleteCookie(AUTH_COOKIE_NAMES.ACTIVE_ARTIST_SLUG);
        deleteCookie(AUTH_COOKIE_NAMES.ACCESS);
        deleteCookie(AUTH_COOKIE_NAMES.REFRESH);
        await store.logout();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Logout API error:", error);
      return false;
    }
  }

  /**
   * Get current user
   */
  static async getCurrentUser(): Promise<User> {
    const response = await apiClient.get(API_ENDPOINTS.USER.PROFILE);
    const user = response.data;

    const store = useAuthStore.getState();
    store.setUser(user);

    return user;
  }

  /**
   * Forgot password
   */
  static async forgotPassword(email: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
  }

  /**
   * Reset password
   */
  static async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      token,
      password: newPassword,
    });
  }

  /**
   * Get artist categories
   */
  static async getCategories(): Promise<Category[]> {
    const response = await apiClient.get(API_ENDPOINTS.AUTH.CATEGORIES);
    return response.data;
  }

  /**
   * Get artist subcategories (genres)
   */
  static async getSubcategories(slug: string): Promise<Category[]> {
    const response = await apiClient.get(
      API_ENDPOINTS.AUTH.SUBCATEGORIES(slug),
    );
    return response.data;
  }

  /**
   * Sign up or sign in via SSO (Google, Truecaller, Instagram, etc.).
   * POST api/v2/auth/signup/sso/ with { provider, token } or { provider, request_id }.
   * Backend sets auth cookies on success. Returns onboarding_completed and user data for routing.
   */
  static async signupSso(
    provider: "google" | "truecaller" | "instagram" | "apple",
    tokenOrRequestId: string,
    appleExtras?: Pick<AppleSsoProfile, "givenName" | "familyName" | "email" | "fullName">,
  ): Promise<SignupSsoResult> {
    const store = useAuthStore.getState();
    store.setLoading(true);

    try {
      const appleFullName =
        appleExtras?.fullName?.trim() ||
        [appleExtras?.givenName, appleExtras?.familyName].filter(Boolean).join(" ").trim() ||
        undefined;

      const payload =
        provider === "truecaller"
          ? { provider, request_id: tokenOrRequestId }
          : provider === "apple" && appleExtras
            ? {
                provider,
                token: tokenOrRequestId,
                ...(appleExtras.givenName ? { first_name: appleExtras.givenName } : {}),
                ...(appleExtras.familyName ? { last_name: appleExtras.familyName } : {}),
                ...(appleExtras.email ? { email: appleExtras.email } : {}),
                ...(appleFullName ? { full_name: appleFullName } : {}),
              }
            : { provider, token: tokenOrRequestId };

      const response = await apiClient.post<SignupSsoResponse>(
        API_ENDPOINTS.AUTH.SIGNUP_SSO,
        payload
      );
      const data = response.data;
      if (!data?.success || !data.data) {
        throw new Error("Invalid SSO response");
      }

      const inner = data.data;
      const parsed = parseAuthResponse(response.data as unknown as Record<string, unknown>);
      await persistAuthTokensFromResponse(parsed.accessToken, parsed.refreshToken);
      const user =
        parsed.user ||
        (inner.user
          ? {
              id: "",
              email: inner.user.email ?? "",
              name: [inner.user.first_name, inner.user.last_name].filter(Boolean).join(" ").trim() || inner.user.email,
              role: (inner.role === "agency" ? "agency" : "artist") as User["role"],
              createdAt: "",
              updatedAt: "",
            }
          : undefined);
      if (user) store.setUser(user);

      const role = (inner.role ?? "individual").trim();
      return {
        onboarding_completed: Boolean(inner.onboarding_completed),
        created: Boolean(inner.created),
        source: String(inner.source ?? provider),
        user: inner.user,
        artist: inner.artist,
        role,
        artists: Array.isArray(inner.artists) ? inner.artists : undefined,
      };
    } catch (error) {
      throw error;
    } finally {
      store.setLoading(false);
    }
  }

  /**
   * Complete final onboarding step.
   * Returns response on 200; use data.user_type to route (Manager/Artist/Family → user-home, Agency → agency-home).
   */
  static async onboarding(data: OnboardingPayload): Promise<OnboardingResponse> {
    const response = await apiClient.post<OnboardingResponse>(
      API_ENDPOINTS.AUTH.ONBOARDING,
      data
    );
    return response.data;
  }

  /**
   * Fetch similar profiles by professional name (for signup stage name step).
   * Supports server-side pagination using `page` and returns count/next/previous.
   *
   * API shapes handled:
   * - { success, data: { results, count, next, previous } }
   * - { results, count, next, previous }
   * - { similar_profiles: [...], count }
   * - [...rawList]
   */
  static async getSimilarProfiles(
    professionalName: string,
    page?: number,
  ): Promise<SimilarProfilesApiResponse> {
    const response = await apiClient.get(
      API_ENDPOINTS.AUTH.SIMILAR_PROFILES,
      {
        params: {
          professional_name: professionalName.trim(),
          ...(page ? { page } : {}),
        },
      },
    );

    const data = response.data as unknown;
    let container: any = data;

    if (data && typeof data === "object") {
      const obj = data as Record<string, unknown>;
      if (obj.data && typeof obj.data === "object") {
        container = obj.data as Record<string, unknown>;
      }
    }

    let rawList: unknown[] = [];
    if (container && typeof container === "object") {
      const obj = container as Record<string, unknown>;
      if (Array.isArray(obj.results)) rawList = obj.results;
      else if (Array.isArray(obj.similar_profiles)) rawList = obj.similar_profiles;
      else if (Array.isArray(obj.data)) rawList = obj.data;
      else if (Array.isArray(data)) rawList = data as unknown[];
    } else if (Array.isArray(data)) {
      rawList = data;
    }

    const items: SimilarProfilesApiItem[] = rawList.map((item) => {
      const o = (item && typeof item === "object" ? item : {}) as Record<string, unknown>;
      const artistUsers = Array.isArray(o.artist_users) ? o.artist_users : [];
      const firstUser = (artistUsers[0] && typeof artistUsers[0] === "object" ? artistUsers[0] : {}) as Record<string, unknown>;
      return {
        id: String(o.slug ?? o.id ?? ""),
        slug: typeof o.slug === "string" ? o.slug : undefined,
        profile_pic: typeof o.profile_pic === "string" ? o.profile_pic : (o.profilePic as string) ?? undefined,
        professional_name: String(o.professional_name ?? o.professionalName ?? o.stageName ?? ""),
        masked_email: typeof firstUser.email === "string" ? firstUser.email : (o.masked_email as string) ?? undefined,
        masked_phone: typeof firstUser.phone === "string" ? firstUser.phone : (o.masked_phone as string) ?? undefined,
      };
    });

    const obj = (container && typeof container === "object" ? container : {}) as Record<string, unknown>;
    const count = typeof obj.count === "number" ? obj.count : items.length;
    const next = typeof obj.next === "string" || obj.next === null ? (obj.next as string | null) : null;
    const previous = typeof obj.previous === "string" || obj.previous === null ? (obj.previous as string | null) : null;
    const pageSize = items.length;

    return { items, count, next, previous, pageSize };
  }

  /**
   * Request OTP for password reset (send to email or phone).
   */
  static async requestPasswordResetOtp(to: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.PASSWORD_RESET_REQUEST_OTP, { to });
  }

  /**
   * Verify OTP for password reset. On 200, caller can show new password step.
   */
  static async verifyPasswordResetOtp(to: string, otp: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.PASSWORD_RESET_VERIFY_OTP, {
      to,
      otp,
    });
  }

  /**
   * Send OTP for profile contact (email or phone) verification.
   * Payload: { to: "<email-or-phone>" }.
   */
  static async sendProfileVerifyOtp(to: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.PROFILE_VERIFY_SEND_OTP, { to });
  }

  /**
   * Verify OTP for profile contact (email or phone).
   * Payload: { to: "<email-or-phone>", otp: "1234" }.
   */
  static async verifyProfileOtp(to: string, otp: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.PROFILE_VERIFY_OTP_VERIFY, {
      to,
      otp,
    });
  }

  /**
   * Set new password after OTP verification. On success, redirect to login.
   */
  static async resetPasswordPut(to: string, password: string): Promise<void> {
    await apiClient.put(API_ENDPOINTS.AUTH.PASSWORD_RESET, { to, password });
  }

  /**
   * Set active artist for current session (agency selecting an artist).
   */
  static async setActiveArtist(artistSlug: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ACTIVE_ARTIST, {
      artist_slug: artistSlug,
    });
  }

  /**
   * Submit claim profile as multipart/form-data (not JSON).
   * Uses fetch so we do not set Content-Type; the browser sets multipart/form-data with the correct boundary.
   */
  static async claimProfile(formData: FormData): Promise<void> {
    const response = await fetch(API_ENDPOINTS.AUTH.CLAIM_PROFILE, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!response.ok) {
      const err = new Error(response.statusText);
      (err as any).response = { status: response.status, data: await response.json().catch(() => ({})) };
      throw err;
    }
  }
}
